﻿import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

// Helper: parse flexible month labels like "2024 Oct" or "Oct 2024" to Date (first day of month)
function parseMonthLabel(label){
  if(!label) return null; const parts=String(label).trim().split(/\s+/); if(parts.length!==2) return null; let year, monTxt; if(/^\d{4}$/.test(parts[0])) { year=+parts[0]; monTxt=parts[1]; } else if(/^\d{4}$/.test(parts[1])) { year=+parts[1]; monTxt=parts[0]; } else return null; const monthIndex = ['jan','feb','mar','apr','may','jun','jul','aug','sep','sept','oct','nov','dec'].indexOf(monTxt.toLowerCase()); if(monthIndex < 0) return null; // normalize sept
  const realMonth = monthIndex > 8 ? monthIndex- (monthIndex===9?0:0) : monthIndex; // monthIndex already correct (0-based) for all except we included 'sept'
  return new Date(year, realMonth, 1);
}

const TenantStatementsChart = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  // Ensure rows are sorted chronologically (ascending) regardless of API order
  const sortedRows = useMemo(() => {
    return [...(rows||[])].sort((a,b)=>{
      const da = parseMonthLabel(a?.monthLabel) || 0; const db = parseMonthLabel(b?.monthLabel) || 0; return da - db;
    });
  }, [rows]);

  // Compute reliability metrics for latest month (last element after sorting)
  const latestMetrics = useMemo(() => {
    if (!sortedRows || sortedRows.length === 0) return null;
    const latestMonth = sortedRows[sortedRows.length - 1]; // FIX: previously used first element
    const totalRuns = latestMonth?.sent || 0; // total statements attempted
    const errors = latestMonth?.errors || 0;  // failed statements
    const successfulRuns = Math.max(0, totalRuns - errors);
    const successRateRaw = totalRuns > 0 ? (successfulRuns / totalRuns) * 100 : 0; // exact
    const reliabilityScore = Math.floor(successRateRaw * 10) / 10; // one decimal precision score
    const failureRateRaw = totalRuns > 0 ? (errors / totalRuns) * 100 : 0;
    return {
      latestMonth,
      totalRuns,
      successfulRuns,
      errors,
      successRateRaw,
      reliabilityScore,
      failureRateRaw,
      errorsPer100: failureRateRaw,
    };
  }, [sortedRows]);

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 99.5) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 98) return { text: 'Strong', color: '#4caf50', icon: '✓' };
    if (score >= 95) return { text: 'Good', color: '#7cb342', icon: 'ℹ' };
    if (score >= 90) return { text: 'Fair', color: '#ffa000', icon: '!' };
    if (score >= 80) return { text: 'Needs Attention', color: '#ff5722', icon: '!' };
    return { text: 'Critical', color: '#d32f2f', icon: '⚠' };
  };

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true); setError('');
      try {
        const res = await apiFetch('/api/tenant-statements/monthly-summary?months=12');
        if (!res.ok) throw new Error('Failed to load tenant statements summary');
        const data = await res.json();
        if (!ignore) setRows(data);
      } catch (e) {
          Sentry.captureException(e);
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [apiFetch]);

  const chartData = useMemo(() => (sortedRows || []).map(r => ({ ...r, success: Math.max(0,(r?.sent||0)-(r?.errors||0)) })), [sortedRows]);

  if (loading) return <HexagonLoader/>;
  if (error) return <div style={{ color:'red' }}>{error}</div>;

  const primary = getComputedStyle(document.documentElement).getPropertyValue('--primary-color')?.trim() || '#F63040';
  const secondary = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color')?.trim() || '#57565B';
  const formatMonthYear = (label) => { if(!label) return ''; const parts=String(label).trim().split(/\s+/); if(parts.length===2){ const [a,b]=parts; if(/^\d{4}$/.test(a)) return `${b} ${a}`; if(/^\d{4}$/.test(b)) return `${a} ${b}`; } return label; };
  
  const reliabilityScore = latestMetrics?.reliabilityScore;
  const scoreInterp = getScoreInterpretation(reliabilityScore);
  const { latestMonth = {}, totalRuns = 0, successfulRuns = 0, errors: failedRuns = 0, successRateRaw = 0, failureRateRaw = 0, errorsPer100 = 0 } = latestMetrics || {};
  const monthLabel = latestMonth.monthLabel || 'Latest Month';

  const llmProxyText = [
    `Automation Reliability Score: ${reliabilityScore}`,
    `Success Rate: ${successRateRaw.toFixed(2)}%`,
    `Failure Rate: ${failureRateRaw.toFixed(2)}%`,
    `Total Runs: ${totalRuns}`,
    `Successful: ${successfulRuns}`,
    `Failed: ${failedRuns}`,
    `Errors per 100: ${errorsPer100.toFixed(2)}`
  ].join('\n');

  return (
    <div className="dashboard-card group-wrapper tenant-statements-chart">
      <div data-llm-proxy style={{ display:'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop: 0, marginBottom: 12 }}>Tenant Statement Reliability & Process Execution</h2>

      {/* Executive Summary Banner */}
      {sortedRows.length > 0 && (
        <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
          <div className="exec-banner-layout">
            <div className="exec-banner-score">
              <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
                {reliabilityScore != null ? `${reliabilityScore.toFixed(1)}%` : '—'}
              </div>
              <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
              <div className="exec-banner-sublabel">Reliability Score ({monthLabel})</div>
            </div>
            <div className="exec-banner-body">
              <div className="exec-banner-text">
                <strong>What this means:</strong> Tenant statement automation achieved a <strong>{successRateRaw.toFixed(2)}% success rate</strong> with <strong>{successfulRuns.toLocaleString()} successful</strong> out of {totalRuns.toLocaleString()} attempts in {monthLabel}. {failedRuns > 0 && `${failedRuns} failures (${failureRateRaw.toFixed(2)}%) detected.`}
                {reliabilityScore >= 99.5 && ' Automation is highly reliable - excellent execution.'}
                {reliabilityScore >= 98 && reliabilityScore < 99.5 && ' Strong reliability with very low error rate.'}
                {reliabilityScore >= 95 && reliabilityScore < 98 && ' Good reliability; investigate recurring issues to reach best-in-class.'}
                {reliabilityScore < 95 && reliabilityScore >= 90 && <span style={{ color: '#ffa000', fontWeight: 600 }}> Reliability below target; address systemic causes.</span>}
                {reliabilityScore < 90 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> Reliability degraded; urgent investigation required.</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      {sortedRows.length > 0 && (
        <div className="metrics-row">
          <div className="metric-card" title="Total statement generation attempts (success + failed)">
            <div className="metric-label">Total Statements</div>
            <div className="metric-value">{totalRuns.toLocaleString()}</div>
            <div className="metric-subtitle">This month</div>
          </div>
          <div className="metric-card" title="Successful statements delivered without errors">
            <div className="metric-label">Successful</div>
            <div className="metric-value" style={{ color: '#2e7d32' }}>{successfulRuns.toLocaleString()}</div>
            <div className="metric-subtitle">Delivered</div>
          </div>
            <div className="metric-card" title="Statements that failed and require investigation">
            <div className="metric-label">Failures</div>
            <div className="metric-value" style={{ color: failedRuns > 0 ? '#d32f2f' : '#666' }}>{failedRuns.toLocaleString()}</div>
            <div className="metric-subtitle">Require investigation</div>
          </div>
          <div className="metric-card" title="Exact success rate (2 decimals)">
            <div className="metric-label">Success Rate</div>
            <div className="metric-value" style={{ color: reliabilityScore >= 99.5 ? '#2e7d32' : reliabilityScore >= 98 ? '#4caf50' : reliabilityScore >= 95 ? '#7cb342' : reliabilityScore >= 90 ? '#ffa000' : '#d32f2f' }}>
              {successRateRaw.toFixed(2)}%
            </div>
            <div className="metric-subtitle">Exact (not rounded)</div>
          </div>
          <div className="metric-card" title="Failures as percentage of total">
            <div className="metric-label">Failure Rate</div>
            <div className="metric-value" style={{ color: failureRateRaw <= 0.5 ? '#2e7d32' : failureRateRaw <= 1 ? '#7cb342' : failureRateRaw <= 2 ? '#ffa000' : '#d32f2f' }}>
              {failureRateRaw.toFixed(2)}%
            </div>
            <div className="metric-subtitle">Errors / total</div>
          </div>
          <div className="metric-card" title="Errors per 100 attempts (same as failure rate)">
            <div className="metric-label">Errors / 100</div>
            <div className="metric-value" style={{ color: errorsPer100 <= 0.5 ? '#2e7d32' : errorsPer100 <= 1 ? '#7cb342' : errorsPer100 <= 2 ? '#ffa000' : '#d32f2f' }}>
              {errorsPer100.toFixed(2)}
            </div>
            <div className="metric-subtitle">Density</div>
          </div>
        </div>
      )}

      {/* Trend Chart */}
      <div className="dashboard-card chart-card">
        <h4 className="chart-title">12-Month Automation Performance Trend</h4>
        <div className="chart-subtitle">Successful executions vs errors - identify patterns and improvements</div>
        <ResponsiveContainer width={isPrintMode ? 680 : "100%"} height={150} minHeight={150}>
          <LineChart data={chartData} margin={{ top:4, right:8, left:0, bottom:0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="monthLabel" tickFormatter={formatMonthYear} tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} />
            <Tooltip labelFormatter={formatMonthYear} formatter={(value, name) => [value.toLocaleString(), name]} />
            <Legend />
            <Line type="monotone" dataKey="success" name="Successful" stroke={secondary} strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />
            <Line type="monotone" dataKey="errors" name="Errors" stroke={primary} strokeWidth={2} dot={false} isAnimationActive={!isPrintMode} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Explanation Section */}
      <div className="explanation-box">
        <strong>Understanding Automation Metrics:</strong>
        <ul>
          <li><strong>Reliability Score:</strong> Truncated success rate (1 decimal). Prevents rounding up to 100% when failures exist.</li>
            <li><strong>Success Rate:</strong> Exact percentage of statements generated without errors (2 decimals).</li>
            <li><strong>Failure Rate:</strong> Percentage of attempts that failed. Target {'<'}0.5%.</li>
            <li><strong>Errors per 100:</strong> Normalizes failures for easier comparison month-to-month.</li>
            <li><strong>Failed Runs:</strong> Count of errors requiring investigation. Each failure wastes staff time and delays tenant communications.</li>
            <li><strong>Error Patterns:</strong> Recurring errors suggest systemic issues (data quality, API timeouts, permissions) vs. one-off data problems.</li>
            <li><strong>Manual Intervention:</strong> When automation fails, staff must manually generate statements - reduces efficiency and increases costs.</li>
            <li><strong>Trend Analysis:</strong> Improving trend shows automation maturing. Declining trend indicates growing data quality issues or system degradation.</li>
        </ul>
        {reliabilityScore != null && reliabilityScore < 98 && (
          <div className="action-callout">
            <strong>Action:</strong> Reliability at {successRateRaw.toFixed(2)}%. Review top failure patterns (e.g. missing tenant data, email delivery issues, upstream API timeouts). Focus on eliminating repetitive causes first.
          </div>
        )}
        {reliabilityScore != null && reliabilityScore >= 99.5 && failedRuns > 0 && (
          <div className="action-callout" style={{ background: '#e8f5e9', borderLeftColor: '#2e7d32' }}>
            <strong>Optimization:</strong> World-class reliability. Investigate whether remaining {failedRuns} failures are preventable (data validation pre-checks, retries, improved monitoring) to reach true 100%.
          </div>
        )}
      </div>

      <div className="data-source">
        Data Source: MDA tenant statement automation logs (last 12 months). Each execution represents an automated statement generation attempt. Success = statement generated and delivered without errors.
      </div>
    </div>
  );
};

export default TenantStatementsChart;
